import { logsModel } from "@/interfaces/zod/logs";
import { store } from "@/redux/store";
import { logsApi } from "@/services/logs.service";
import { z } from "zod";
import Bowser from "bowser";
import { getClientChannelHeader, getClientContext } from "./client-context";
import { now } from "./date-utils";
import { getApiBaseUrl } from "./rtk-query-config";
import { urlSemTokenInscricao } from "./inscricao-acesso";
import { createLogQueue, type LogItem, type ResultadoEnvio } from "./log-queue";

async function enviarLote(itens: LogItem[]): Promise<ResultadoEnvio> {
  try {
    await store.dispatch(logsApi.endpoints.postLogsLote.initiate({ logs: itens })).unwrap();
    return { status: 201 };
  } catch (err) {
    const status = (err as { status?: unknown })?.status;
    return { status: typeof status === "number" ? status : 0 };
  }
}

const filaLogs = createLogQueue({ send: enviarLote });

if (typeof window !== "undefined") {
  // Sai da aba/pagina: manda o que estiver na fila sem esperar o debounce.
  window.addEventListener("pagehide", () => void filaLogs.flush());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void filaLogs.flush();
  });
}

function buildLogObjeto(
  objeto: Record<string, unknown>,
  options?: { includeUser?: boolean },
) {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const deviceInfo = browser.getResult();
  const clientContext = getClientContext();
  const includeUser = options?.includeUser !== false;
  return {
    ...clientContext,
    ...objeto,
    url: urlSemTokenInscricao(window.location.href),
    title: document.title,
    referrer: urlSemTokenInscricao(document.referrer),
    userAgent: navigator.userAgent,
    date: now(),
    device: {
      name: deviceInfo.browser.name || "Unknown Browser",
      version: deviceInfo.browser.version || "Unknown Version",
      os: deviceInfo.os.name || "Unknown OS",
      osVersion: deviceInfo.os.versionName || "Unknown Version",
      platform: deviceInfo.platform.type || "Unknown Platform",
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    language: navigator.language,
    user: includeUser
      ? (() => {
          const auth = store.getState().authReducer;
          if (!auth?.id) return null;
          return {
            id: auth.id,
            name: auth.name ?? null,
            email: auth.email ?? null,
            Master: auth.Master ?? null,
          };
        })()
      : null,
  };
}

export async function postLog(log: z.infer<typeof logsModel>) {
  const sessionLoginId = store.getState().generalReducer?.LoginId;
  const hasExplicitLoginId =
    log.LoginId !== undefined && log.LoginId !== null && log.LoginId > 0;
  const loginId = hasExplicitLoginId ? log.LoginId : sessionLoginId;
  // Páginas públicas com LoginId explícito: sem UserId (FK composta).
  const userId = hasExplicitLoginId
    ? undefined
    : (log.UserId ?? store.getState().authReducer?.id);

  // Enfileira (lote com debounce); nunca rejeita, log nao pode quebrar fluxo.
  return filaLogs.enqueue({
    ...(loginId ? { LoginId: loginId } : {}),
    ...(userId ? { UserId: userId } : {}),
    Tipo: log.Tipo,
    Objeto: buildLogObjeto((log.Objeto ?? {}) as Record<string, unknown>, {
      includeUser: !hasExplicitLoginId,
    }),
  });
}

/** POST /logs sem Authorization — landings públicas (RSVP etc.). */
export async function postAnonymousLog(log: {
  Tipo: string;
  LoginId: number;
  Objeto?: Record<string, unknown>;
}) {
  const response = await fetch(`${getApiBaseUrl()}/logs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Client-Channel": getClientChannelHeader(),
    },
    body: JSON.stringify({
      LoginId: log.LoginId,
      Tipo: log.Tipo,
      Objeto: buildLogObjeto(log.Objeto ?? {}, { includeUser: false }),
    }),
  });

  if (!response.ok) {
    throw new Error(`Falha ao gravar log anônimo (${response.status})`);
  }

  return response.json().catch(() => ({ ok: true }));
}
